import { Box, Button, CircularProgress, Divider, FormControlLabel, Switch, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import { setError, setSuccess } from "../../actions/notificationActions";
import { getEmailPreferences, updateEmailPreferences } from "../../util/api/userApi";


const preferenceControlStyles = {
  color: "#686868",
  "& .MuiSwitch-switchBase.Mui-checked": {
    color: "#24211e",
  },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: "#24211e",
    opacity: 0.55,
  },
  "& .MuiSwitch-track": {
    backgroundColor: "#686868",
    opacity: 0.4,
  },
};

const preferenceCardStyles = {
  backgroundColor: "#f7f7f7",
  border: "1px solid #d5d5d5",
  borderRadius: 2,
  p: 2,
};

const EmailPreferences = () => {
  const [preferences, setPreferences] = useState(null);
  const [savedPreferences, setSavedPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    getEmailPreferences().then((response) => {
      setPreferences(response.data);
      setSavedPreferences(response.data);
    }).catch(() => {
      dispatch(setError("Could not load email preferences."));
    }).finally(() => {
      setLoading(false);
    });
  }, [dispatch]);

  const hasChanges = preferences && savedPreferences && (
    preferences.marketing_emails !== savedPreferences.marketing_emails
    || preferences.product_updates !== savedPreferences.product_updates
  );

  const handleSave = () => {
    if (!hasChanges || saving)
      return;

    setSaving(true);
    updateEmailPreferences(
      preferences.marketing_emails,
      preferences.product_updates,
    ).then((response) => {
      setPreferences(response.data);
      setSavedPreferences(response.data);
      dispatch(setSuccess("Email preferences updated!"));
    }).catch((error) => {
      dispatch(setError(error.response?.data?.detail || "Could not update email preferences."));
    }).finally(() => {
      setSaving(false);
    });
  };

  return <Box sx={{ backgroundColor: "#fff", color: "#171717", mb: 5 }}>
    <Typography sx={{ color: "#171717", fontWeight: 600 }} variant="h4">
      Email preferences
    </Typography>
    <Divider sx={{ borderColor: "#b8b8b8", mt: 1, mb: 3 }} />

    {loading && <CircularProgress aria-label="Loading email preferences" sx={{ color: "black" }} />}
    {!loading && !preferences && <Typography sx={{ color: "#171717" }}>
      Email preferences are currently unavailable.
    </Typography>}
    {preferences && <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={preferenceCardStyles}>
        <FormControlLabel
          control={<Switch
            checked={preferences.marketing_emails}
            onChange={(event) => setPreferences({ ...preferences, marketing_emails: event.target.checked })}
            sx={preferenceControlStyles}
          />}
          label={<Typography sx={{ color: "#171717", fontWeight: 600 }}>
            Marketing emails
          </Typography>}
          sx={{ m: 0 }}
        />
        <Typography sx={{ color: "#4f4f4f", fontSize: "0.8rem", ml: 6 }}>
          Product announcements, promotions, and other WaveMind news.
        </Typography>
      </Box>

      <Box sx={preferenceCardStyles}>
        <FormControlLabel
          control={<Switch
            checked={preferences.product_updates}
            onChange={(event) => setPreferences({ ...preferences, product_updates: event.target.checked })}
            sx={preferenceControlStyles}
          />}
          label={<Typography sx={{ color: "#171717", fontWeight: 600 }}>
            Product updates
          </Typography>}
          sx={{ m: 0 }}
        />
        <Typography sx={{ color: "#4f4f4f", fontSize: "0.8rem", ml: 6 }}>
          System messages about compatibility, security, and version updates for your products.
        </Typography>
      </Box>

      <Typography sx={{ color: "#4f4f4f", fontSize: "0.75rem" }}>
        Essential account, purchase, license, and password emails cannot be disabled.
      </Typography>

      <Button
        disabled={!hasChanges || saving}
        onClick={handleSave}
        sx={{
          alignSelf: "flex-start",
          backgroundColor: "#24211e",
          color: "#fff",
          "&:hover": {
            backgroundColor: "#3a3733",
          },
          "&.Mui-disabled": {
            backgroundColor: "#dedede",
            color: "#606060",
          },
        }}
        variant="contained"
      >
        {saving ? "Saving…" : "Save preferences"}
      </Button>
    </Box>}
  </Box>;
}

export default EmailPreferences;
